'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useMemo, } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { deriveIntent, fromHex, THEME_IDS, themes, toCss, } from '@rookcom/monarch-tokens';
import { cx } from '../../../internal/cx.js';
import { Spinner } from '../../feedback/spinner/index.js';
const THEME_KEY = {
    light: 'l',
    dark: 'd',
    'hc-light': 'hl',
    'hc-dark': 'hd',
};
/** Parses `#rgb` / `#rrggbb`; null when the value is not a hex colour. */
function parseHex(value) {
    if (!value)
        return null;
    const v = value.trim().replace(/^#/, '');
    const six = /^[0-9a-f]{3}$/i.test(v) ? v.replace(/./g, (c) => c + c) : v;
    if (!/^[0-9a-f]{6}$/i.test(six))
        return null;
    return fromHex(six);
}
/**
 * Per-theme custom properties for a brand colour: fill, hover, active and
 * text for each of the four Monarch themes. button.css picks the set that
 * matches the theme on an ancestor (`.dark`, `data-mn-theme`, `data-mn-contrast`).
 */
function brandVars(color) {
    const base = parseHex(color);
    if (!base)
        return undefined;
    const vars = {};
    for (const id of THEME_IDS) {
        const { scale } = deriveIntent(base, themes[id]);
        const k = THEME_KEY[id];
        vars[`--_brand-${k}-bg`] = toCss(scale.solid);
        vars[`--_brand-${k}-bg-hover`] = toCss(scale['solid-hover']);
        vars[`--_brand-${k}-bg-active`] = toCss(scale['solid-active']);
        vars[`--_brand-${k}-fg`] = toCss(scale['on-solid']);
    }
    return vars;
}
/**
 * The primary action control.
 *
 * Defaults to `type="button"` so a button inside a form never submits by
 * accident; pass `type="submit"` explicitly.
 */
export const Button = forwardRef(function Button({ variant = 'secondary', size = 'md', loading = false, leadingIcon, trailingIcon, block = false, asChild = false, brandColor, disabled, type, className, style, children, onClick, ...rest }, ref) {
    const Comp = asChild ? Slot : 'button';
    const brand = variant === 'brand';
    const derived = useMemo(() => (brand ? brandVars(brandColor) : undefined), [brand, brandColor]);
    // A loading button stays focusable (aria-disabled, not disabled) so keyboard
    // users do not lose their place while the action completes.
    const blocked = disabled || loading;
    return (_jsxs(Comp, { ref: ref, ...rest, ...(asChild ? {} : { type: type ?? 'button', disabled: disabled || undefined }), "aria-disabled": blocked || undefined, "aria-busy": loading || undefined, "data-variant": variant, "data-size": size, "data-loading": loading || undefined, "data-block": block || undefined, "data-brand": derived ? '' : undefined, style: derived ? { ...derived, ...style } : style, className: cx('mn-button', className), onClick: (event) => {
            if (blocked) {
                event.preventDefault();
                return;
            }
            onClick?.(event);
        }, children: [loading ? (_jsx(Spinner, { size: "sm", className: "mn-button__icon" })) : leadingIcon ? (_jsx("span", { className: "mn-button__icon", "aria-hidden": true, children: leadingIcon })) : null, _jsx(Slottable, { children: children }), trailingIcon ? (_jsx("span", { className: "mn-button__icon", "aria-hidden": true, children: trailingIcon })) : null] }));
});
/** Square button containing only an icon. `label` is required by the type system. */
export const IconButton = forwardRef(function IconButton({ label, icon, variant = 'ghost', className, ...rest }, ref) {
    return (_jsx(Button, { ref: ref, ...rest, variant: variant, "aria-label": label, className: cx('mn-icon-button', className), children: rest.loading ? null : (_jsx("span", { className: "mn-button__icon", "aria-hidden": true, children: icon })) }));
});
//# sourceMappingURL=button.js.map